import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Form, Formik } from "formik";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RowSelectionState } from "react-table-8.10.7";
import {
  useCategorizeGroupTransactionsMutation,
  useGetAllTransactionsDataQuery,
} from "store/apis/transactions";
import { setSelectedTransactionIds } from "store/slices/transactions";
import { RootState } from "store/store";
import { Transactions } from "types/Models/books";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { TransactionsView } from "components/TransactionsView/TransactionsView";
import { useGetTransactionsFilterSet } from "hooks/useGetTransactionsFilterSet";

type UncategoriseModalProps = ModalProps & {
  deselectRows: () => void;
  unSelectedRowsIds: string[];
  isAllTxnSelected: boolean;
  transactions?: Transactions[];
};

const isSplitTransaction = (txn: Transactions) =>
  Boolean(txn.split_transactions?.length);

const isInvoiceLinkedTransaction = (txn: Transactions) =>
  Boolean(txn.has_linked_invoices);

// Split parents carry no category themselves, their splits do
const isCategorisedTransaction = (txn: Transactions) =>
  (Boolean(txn.transaction.category?.uuid) || isSplitTransaction(txn)) &&
  !isInvoiceLinkedTransaction(txn);

const hasLinkedTransaction = (txn: Transactions) =>
  Boolean(txn.transaction.linked_transaction) ||
  Boolean(txn.split_transactions?.some((split) => split.linked_transaction));

const pluralise = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? "" : "s"}`;

const DisclaimerUI = ({
  txnCount,
  previouslyLinkedTxnsCount,
  splitTxnsCount,
  invoiceLinkedTxnsCount,
}: {
  txnCount: number;
  previouslyLinkedTxnsCount: number;
  splitTxnsCount: number;
  invoiceLinkedTxnsCount: number;
}) => {
  const includes = [
    splitTxnsCount > 0 && pluralise(splitTxnsCount, "split transaction"),
    previouslyLinkedTxnsCount > 0 &&
      pluralise(previouslyLinkedTxnsCount, "linked transaction"),
  ].filter(Boolean);

  return (
    <div className="t-text-body-sm t-flex t-flex-col t-gap-2">
      {txnCount === 0 && invoiceLinkedTxnsCount > 0 ? (
        <div>None of the selected transactions can be uncategorized.</div>
      ) : (
        <div>
          We found{" "}
          <b className="t-text-red t-text-subtitle-sm">
            {pluralise(txnCount, "categorized transaction")}
          </b>
          {includes.length > 0 && (
            <>
              {" "}
              that includes <b>{includes.join(" and ")}</b>
            </>
          )}
          . Are you sure you want to uncategorize them all?
        </div>
      )}
      {splitTxnsCount > 0 && (
        <div>
          All splits will be removed and the split transactions will be
          uncategorized.
        </div>
      )}
      {previouslyLinkedTxnsCount > 0 && (
        <div>
          Linked Bank Transfer and Pay Down Credit transactions will be
          unlinked, and their linked counterparts will be uncategorized too.
        </div>
      )}
      {invoiceLinkedTxnsCount > 0 && (
        <div className="t-text-text-30">
          {pluralise(invoiceLinkedTxnsCount, "transaction")} linked to an
          invoice or bill will be skipped. Unlink the invoice or bill first to
          uncategorize {invoiceLinkedTxnsCount === 1 ? "it" : "them"}.
        </div>
      )}
    </div>
  );
};

const Disclaimer = ({
  transactions,
  selectedTxnIds,
}: {
  transactions: Transactions[];
  selectedTxnIds: string[];
}) => {
  const selectedTxns = transactions.filter((txn) =>
    selectedTxnIds.includes(txn.transaction.uuid)
  );
  const selectedTransactions = selectedTxns.filter(isCategorisedTransaction);

  return (
    <DisclaimerUI
      txnCount={selectedTransactions.length}
      splitTxnsCount={selectedTransactions.filter(isSplitTransaction).length}
      previouslyLinkedTxnsCount={
        selectedTransactions.filter(hasLinkedTransaction).length
      }
      invoiceLinkedTxnsCount={
        selectedTxns.filter(isInvoiceLinkedTransaction).length
      }
    />
  );
};

export const UncategoriseModal = ({
  close,
  isOpen,
  deselectRows,
  isAllTxnSelected,
  unSelectedRowsIds,
  transactions: transactionsFromProps,
}: UncategoriseModalProps) => {
  const dispatch = useDispatch();
  const { successToast, alertToast } = useToast();
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const [groupCategorize] = useCategorizeGroupTransactionsMutation();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { selectedTransactionIds } = useSelector(
    (store: RootState) => store.transactions
  );

  useEffect(() => {
    if (selectedTransactionIds) {
      const selectedTxns = selectedTransactionIds.reduce(
        (accumulator, currentId, i) => {
          return { ...accumulator, [currentId]: true };
        },
        {}
      );
      setRowSelection(selectedTxns);
    }
  }, [selectedTransactionIds]);

  const { data, isLoading: isFilterSetLoading } = useGetTransactionsFilterSet({
    filter_operation: "UNCATEGORIZE",
    unSelectedRowsIds,
    skip: !isAllTxnSelected || !isOpen,
  });

  const {
    previously_categorized_txns_count = 0,
    txns_hash_key = "",
    categorized_txns_with_linked_transaction_count = 0,
    split_txns_count = 0,
    invoice_linked_txns_count = 0,
  } = data || {};

  const { data: transactionData, isLoading } = useGetAllTransactionsDataQuery(
    {
      groupId,
      entityId,
      transactionIds: selectedTransactionIds.join(","),
    },
    {
      skip:
        !groupId ||
        !entityId ||
        isAllTxnSelected ||
        Boolean(transactionsFromProps),
      refetchOnMountOrArgChange: true,
    }
  );

  const { transactions = [] } = transactionData || {};

  const activeTransactions = transactionsFromProps ?? transactions;

  const allCategoriseTransactions = activeTransactions
    .filter(isCategorisedTransaction)
    .map(({ transaction: { uuid } }) => uuid);

  const selectedTxnIds = Object.keys(rowSelection);

  const categorisedSelectedTxnIds = Object.keys(rowSelection).filter((id) =>
    allCategoriseTransactions.includes(id)
  );

  const onClose = () => {
    close();
    dispatch(setSelectedTransactionIds([]));
  };
  const onSubmit = async ({ txns_hash_key }: { txns_hash_key: string }) => {
    try {
      await groupCategorize({
        groupId,
        entityId,
        payload: {
          transaction_category_uuid: null,
          transaction_ids: txns_hash_key
            ? undefined
            : categorisedSelectedTxnIds.join(", "),
          transactions_hash_key: txns_hash_key,
        },
      }).unwrap();
      onClose();
      deselectRows();
      successToast({
        message: "Transactions Uncategorised",
      });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Formik
        initialValues={{ txns_hash_key }}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ submitForm, isSubmitting }) => (
          <Form>
            <Modal.Content size="xl">
              <Modal.Header className="sm:t-pl-4">
                <Modal.Title>Uncategorise</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body className="t-flex t-gap-6 t-flex-col t-text-body-sm sm:t-px-4">
                {isFilterSetLoading ? (
                  <Loader size="small" />
                ) : (
                  <>
                    {isLoading ? (
                      <Loader />
                    ) : (
                      <>
                        {isAllTxnSelected ? (
                          <DisclaimerUI
                            previouslyLinkedTxnsCount={
                              categorized_txns_with_linked_transaction_count
                            }
                            splitTxnsCount={split_txns_count}
                            invoiceLinkedTxnsCount={invoice_linked_txns_count}
                            txnCount={previously_categorized_txns_count}
                          />
                        ) : (
                          <>
                            {
                              <>
                                <Disclaimer
                                  transactions={activeTransactions}
                                  selectedTxnIds={selectedTxnIds}
                                />
                                <TransactionsView
                                  transactions={activeTransactions}
                                  showCheckbox
                                  rowSelection={rowSelection}
                                  setRowSelection={setRowSelection}
                                  disableUncategoriseTransaction
                                />
                              </>
                            }
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </Modal.Body>
              <Modal.Footer className="sm:t-px-4">
                <div className="t-flex t-justify-end t-gap-3">
                  <Button
                    type="reset"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    customType="primary"
                    onClick={submitForm}
                    isLoading={isSubmitting}
                    disabled={
                      isSubmitting || categorisedSelectedTxnIds.length === 0
                    }
                  >
                    Uncategorise
                  </Button>
                </div>
              </Modal.Footer>
            </Modal.Content>
          </Form>
        )}
      </Formik>
    </Modal.Root>
  );
};
